import type { RoofConfig } from '../../engines/types';

export type RectWallName = 'front' | 'back' | 'left' | 'right';

/** The default mono high side: the historical pulpettak rises toward +Z. */
export const DEFAULT_MONO_HIGH_WALL: RectWallName = 'front';

const OPPOSITE_WALL: Record<RectWallName, RectWallName> = {
  front: 'back',
  back: 'front',
  left: 'right',
  right: 'left',
};

export function getOppositeRectWall(wall: RectWallName): RectWallName {
  return OPPOSITE_WALL[wall];
}

export function isRectWallName(value: unknown): value is RectWallName {
  return value === 'front' || value === 'back' || value === 'left' || value === 'right';
}

/**
 * The wall a mono (pulpet) roof is high at, in the building's LOCAL frame
 * (it turns with the building's rotation). Absent means the historical
 * default (`front`), so every design saved before the field existed keeps
 * its shape.
 */
export function resolveMonoHighWall(roofConfig: RoofConfig | undefined | null): RectWallName {
  if (!roofConfig || roofConfig.kind !== 'mono') return DEFAULT_MONO_HIGH_WALL;
  return isRectWallName(roofConfig.highWall) ? roofConfig.highWall : DEFAULT_MONO_HIGH_WALL;
}

/**
 * How a mono roof falls, in world (building-local) axes. `axis` is the
 * horizontal axis the roof RISES along and `sign` which way (+1 = toward
 * +axis). `runLength` is the horizontal distance from the low wall line to the
 * high wall line — `length` for a front/back high wall, `width` for a
 * left/right one — and `eaveLength` the length of the two level eave walls.
 * The two other walls are the raked ones.
 *
 * `signedPitchRad` is what `addSlopedBox(..., signedPitchRad, axis)` needs to
 * shear a box so it rises toward the high wall.
 */
export interface MonoSlope {
  highWall: RectWallName;
  lowWall: RectWallName;
  axis: 'x' | 'z';
  sign: 1 | -1;
  signedPitchRad: number;
  runLength: number;
  eaveLength: number;
  rakedWalls: readonly [RectWallName, RectWallName];
}

export function resolveMonoSlope(
  width: number,
  length: number,
  roofConfig: RoofConfig,
): MonoSlope | null {
  if (roofConfig.kind !== 'mono') return null;
  const highWall = resolveMonoHighWall(roofConfig);
  const axis = highWall === 'front' || highWall === 'back' ? 'z' : 'x';
  const sign = highWall === 'front' || highWall === 'right' ? 1 : -1;
  const pitchRad = (roofConfig.pitchDeg * Math.PI) / 180;
  return {
    highWall,
    lowWall: OPPOSITE_WALL[highWall],
    axis,
    sign,
    signedPitchRad: sign * pitchRad,
    runLength: axis === 'z' ? length : width,
    eaveLength: axis === 'z' ? width : length,
    rakedWalls: axis === 'z' ? ['left', 'right'] : ['front', 'back'],
  };
}

/**
 * Horizontal run a roof's pitch is applied over, eave → ridge / high edge:
 * half the width for a gable, the mono slope run (length or width, by the
 * high wall) for a pulpettak, 0 for a barrel.
 */
export function getRoofSlopeRunM(width: number, length: number, roofConfig: RoofConfig): number {
  if (roofConfig.kind === 'gable') return width / 2;
  return resolveMonoSlope(width, length, roofConfig)?.runLength ?? 0;
}

interface CreateRectRoofGeometryInput {
  width: number;
  length: number;
  wallHeight: number;
  roofConfig: RoofConfig;
}

export interface RectRoofGeometry {
  roofConfig: RoofConfig;
  pitchRad: number;
  roofRise: number;
  ridgeHeight: number;
  /** Mono fall direction; `null` for gable and barrel. */
  monoSlope: MonoSlope | null;
  getRoofHeightAt(x: number, z: number): number;
  getWallHeightAt(wall: RectWallName, localAxis: number): number;
  getMaxWallHeight(wall: RectWallName): number;
  getMinWallHeight(wall: RectWallName): number;
}

export function createRectRoofGeometry({
  width,
  length,
  wallHeight,
  roofConfig,
}: CreateRectRoofGeometryInput): RectRoofGeometry {
  const pitchRad = (roofConfig.pitchDeg * Math.PI) / 180;

  if (roofConfig.kind === 'gable') {
    const roofRise = (width / 2) * Math.tan(pitchRad);
    const ridgeHeight = wallHeight + roofRise;

    const getRoofHeightAt = (x: number) => {
      const halfWidth = width / 2;
      const distanceToCenter = Math.min(halfWidth, Math.abs(x));
      const t = 1 - distanceToCenter / Math.max(halfWidth, 0.0001);
      return wallHeight + roofRise * t;
    };

    return {
      roofConfig,
      pitchRad,
      roofRise,
      ridgeHeight,
      monoSlope: null,
      getRoofHeightAt: (x) => getRoofHeightAt(x),
      getWallHeightAt: (wall, localAxis) => {
        if (wall === 'front' || wall === 'back') {
          return getRoofHeightAt(-width / 2 + localAxis);
        }
        return wallHeight;
      },
      getMaxWallHeight: (wall) => (wall === 'front' || wall === 'back' ? ridgeHeight : wallHeight),
      getMinWallHeight: () => wallHeight,
    };
  }

  const monoSlope = resolveMonoSlope(width, length, roofConfig);

  if (!monoSlope || monoSlope.highWall === DEFAULT_MONO_HIGH_WALL) {
    // Historical shape (and barrel, rise 0): high at the front (+Z), low at
    // the back, the left/right side walls raked. Kept verbatim so every
    // existing design is bit-for-bit unchanged.
    const roofRise = monoSlope ? length * Math.tan(pitchRad) : 0;
    const lowHeight = wallHeight;
    const highHeight = wallHeight + roofRise;
    const ridgeHeight = highHeight;

    return {
      roofConfig,
      pitchRad,
      roofRise,
      ridgeHeight,
      monoSlope,
      getRoofHeightAt: (_x, z) => {
        const t = (z - (-length / 2)) / Math.max(length, 0.0001);
        return lowHeight + t * roofRise;
      },
      getWallHeightAt: (wall, localAxis) => {
        if (wall === 'front') return highHeight;
        if (wall === 'back') return lowHeight;
        const z = -length / 2 + localAxis;
        return lowHeight + ((z - (-length / 2)) / Math.max(length, 0.0001)) * roofRise;
      },
      getMaxWallHeight: (wall) => (wall === 'front' ? highHeight : wall === 'back' ? lowHeight : highHeight),
      getMinWallHeight: (wall) => (wall === 'front' ? highHeight : wall === 'back' ? lowHeight : lowHeight),
    };
  }

  // Any other high wall: the low wall keeps the full wall height (the wall
  // line datum, 2026-04-03) and the roof rises linearly over the slope run.
  // Unclamped on purpose — the overhang and the joist plane extrapolate.
  const { axis, sign, runLength } = monoSlope;
  const roofRise = runLength * Math.tan(pitchRad);
  const highHeight = wallHeight + roofRise;
  const getRoofHeightAt = (x: number, z: number) => {
    const coordinate = axis === 'x' ? x : z;
    const t = (sign * coordinate + runLength / 2) / Math.max(runLength, 0.0001);
    return wallHeight + t * roofRise;
  };
  const wallEnds = (wall: RectWallName): [number, number] => {
    if (wall === 'front' || wall === 'back') {
      const z = wall === 'front' ? length / 2 : -length / 2;
      return [getRoofHeightAt(-width / 2, z), getRoofHeightAt(width / 2, z)];
    }
    const x = wall === 'right' ? width / 2 : -width / 2;
    return [getRoofHeightAt(x, -length / 2), getRoofHeightAt(x, length / 2)];
  };

  return {
    roofConfig,
    pitchRad,
    roofRise,
    ridgeHeight: highHeight,
    monoSlope,
    getRoofHeightAt,
    getWallHeightAt: (wall, localAxis) => {
      if (wall === 'front' || wall === 'back') {
        return getRoofHeightAt(-width / 2 + localAxis, wall === 'front' ? length / 2 : -length / 2);
      }
      return getRoofHeightAt(wall === 'right' ? width / 2 : -width / 2, -length / 2 + localAxis);
    },
    getMaxWallHeight: (wall) => Math.max(...wallEnds(wall)),
    getMinWallHeight: (wall) => Math.min(...wallEnds(wall)),
  };
}
